import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ComboBox, ConfirmDialog, Select } from "@activecollab/components";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import { LabelTrigger } from "~/components/ListView/columns/LabelTrigger";
import { ListButton } from "~/components/ListView/ListButton";
import Label from "~/records/record.label";
import { addLabel } from "~/store/actions/actions.labels";
import { getAllGlobaTaskLabelsSortedByName } from "~/store/selectors/selector.labels";
import { TemplateTaskPanelItem } from "./TemplateTaskPanelItem";

const SelectLabelTrigger = React.forwardRef(({ labels, open, onClick }, ref) => {
  const handleOpen = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    },
    [onClick]
  );

  return (
    <ListButton
      variant="text gray"
      className={classNames("tw-flex tw-justify-end tw-py-1 tw-px-2 tw-overflow-visible", {
        "tw-w-auto tw-max-w-full": labels.length > 0,
        "tw-w-auto show-on-hover": !labels.length,
        open,
      })}
      open={open}
      onClick={handleOpen}
      ref={ref}
      data-testid="task-list-labels"
    >
      <LabelTrigger labels={labels} />
    </ListButton>
  );
});

SelectLabelTrigger.propTypes = {
  labels: PropTypes.array,
  open: PropTypes.bool,
  onClick: PropTypes.func,
};

SelectLabelTrigger.defaultProps = {
  labels: [],
};

SelectLabelTrigger.displayName = "SelectLabelTrigger";

export const TemplateTaskLabelPicker = ({ task, listView, onChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const previous = useRef(task.labels);

  const labels = useSelector(getAllGlobaTaskLabelsSortedByName);

  const [open, setOpen] = useState();
  const [selectedLabels, setSelectedLabels] = useState(task.labels);

  useEffect(() => {
    if (!isEqual([...previous.current].sort(), [...task.labels].sort())) {
      return setSelectedLabels(task.labels);
    }
  }, [task.labels]);

  const labelOptions = labels.map((label) => ({
    id: label.id,
    name: label.name,
    color: label.lighter_text_color,
    textColor: label.lighter_text_color,
    chipColor: "#303037",
  }));

  const handleChange = useCallback((ids) => {
    setSelectedLabels(ids);
  }, []);

  const handleSave = useCallback(() => {
    if (!isEqual([...selectedLabels].sort(), [...previous.current].sort())) {
      onChange({ labels: selectedLabels });
      previous.current = selectedLabels;
    }
  }, [onChange, selectedLabels]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setSelectedLabels([]);
    setOpen(false);
  }, []);

  const handleAddNewLabel = useCallback(
    (labelName) => {
      const newLabel = {
        name: labelName.toUpperCase(),
        type: "TaskLabel",
        is_global: true,
        is_default: false,
      };

      dispatch(addLabel(newLabel)).then((data) => {
        const { response } = data;
        if (response.result?.single && response.entities.labels[response.result.single]) {
          const label = new Label(response.entities.labels[response.result.single]);

          const newSelected = [...task.labels];
          newSelected.push(label.get("id"));

          setSelectedLabels(newSelected);
        }
      });
    },
    [dispatch, task.labels]
  );

  return (
    <>
      {listView ? (
        <Select
          options={labelOptions}
          onChange={handleChange}
          type="multiple"
          emptyValue={t("Add new label")}
          emptyAction={handleAddNewLabel}
          selected={selectedLabels}
          autoHeightMax={278}
          onSelectClose={handleSave}
          keepSameOptionsOrder
          target={<SelectLabelTrigger labels={selectedLabels} />}
        />
      ) : (
        <TemplateTaskPanelItem label={t("Labels")}>
          <ComboBox
            type="multiple"
            inputWrapperClassName="tw-w-auto"
            onChange={handleChange}
            selected={selectedLabels}
            options={labelOptions}
            emptyValue={t("Add new label")}
            handleEmptyAction={handleAddNewLabel}
            placeholder={`${t("Add")}...`}
            scrollWrapper
            onDeselect={handleOpen}
            onClose={handleSave}
          />
        </TemplateTaskPanelItem>
      )}
      {open && (
        <ConfirmDialog
          open={open}
          onCancel={handleClose}
          onConfirm={handleConfirm}
          confirmBtnText={t("Clear")}
          cancelBtnText={t("Cancel")}
          dialogTitle={t("Clear All Labels?")}
          dialogContent={t("All labels will be removed from this task.")}
        />
      )}
    </>
  );
};

TemplateTaskLabelPicker.propTypes = {
  task: PropTypes.object.isRequired,
  listView: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

TemplateTaskLabelPicker.defaultProps = {
  listView: false,
};
